import loadable from '@loadable/component'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { NIL as NIL_UUID } from 'uuid'

import { setAuthTokenHeader } from 'api/http'
import { selectAuth, TokenPayload } from 'features/login/authSlice'
import { BrowserRouter as Router, Redirect, Route, RouteProps, Switch } from 'react-router-dom'
import { LaptopWindows } from '@material-ui/icons'

const LoginPage = loadable(() => import('features/login/LoginPage'))
const UsersPage = loadable(() => import('features/users/UsersPage'))
const LicensesPage = loadable(() => import('features/licenses/LicensesPage'))
const TOSPage = loadable(() => import('features/tos/ToSPage'))

const SignUpPage = loadable(() => import('features/accounts/SignUpPage'))
const SignUpCompletedPage = loadable(() => import('features/accounts/SignUpCompletedPage'))
const EmailConfirmationPage = loadable(() => import('features/accounts/EmailConfirmationPage'))
const AccountTransferConfirmationPage = loadable(() => import('features/accounts/AccountTransferConfirmationPage'))
const AccountTransferSignUpPage = loadable(() => import('features/accounts/AccountTransferSignUpPage'))
const ResetPasswordPage = loadable(() => import('features/accounts/ResetPasswordPage'))
const ForgotPasswordPage = loadable(() => import('features/accounts/ForgotPasswordPage'))
const SignInPage = loadable(() => import('features/accounts/SignInPage'))
const ProfilesPage = loadable(() => import('features/profiles/ProfilesPage'))

const PrivateRoute = (props: RouteProps) => {
  const { path, ...rest } = props
  // TODO: try to read token from localstorage: if rember me checkbox was checked.
  const { token, payload, payloadV2, systemVersion } = useSelector(selectAuth)
  const now = dayjs().unix()

  let tokenPayload
  if (systemVersion === 'v2') {
    tokenPayload = payloadV2
  } else {
    tokenPayload = payload
  }
  const { exp } = tokenPayload
  if (tokenPayload && exp && now < exp) {
    setAuthTokenHeader(token)
    if (systemVersion === 'v2') {
      const { groupTag, groupUUID, migrate } = tokenPayload as TokenPayload

      // 0.groupTag不會是空值，其attributType在groupTagIndex(GSI)中已經被設定為S
      // 1.剛註冊完帳號後的使用者，groupTag會是NIL_UUID，groupUUID會由(uuid v4)產生值
      // 2.轉移帳號後的使用者, 其groupTag和groupUUID都會是NIL_UUID
      if (groupTag === NIL_UUID && groupUUID !== NIL_UUID) {
        return <ProfilesPage helpDialog={'GroupTagHelpDialog'} />
      }
      if (migrate) {
        return <ProfilesPage helpDialog={'MigrateProgressDialog'} />
      }
    }

    return <Route exact path={path} {...rest} />
  } else {
    console.log('Redirect: /signin')
    return <Redirect to="/signin" />
  }
}

export type DeviceProfileUrlParam = {
  deviceID: string
  tab?: string
}

const NoMatch = () => <div>This No Match page.</div>

export default function MainRouter() {
  return (
    <Router basename="">
      <Switch>
        <Route exact path="/login" component={LoginPage} />
        <Route exact path="/signin" component={SignInPage} />
        <Route exact path="/signup" component={SignUpPage} />
        <Route exact path="/signup-completed" component={SignUpCompletedPage} />
        <Route exact path="/email-confirmation" component={EmailConfirmationPage} />
        <Route exact path="/account-transfer-confirmation" component={AccountTransferConfirmationPage} />
        <Route exact path="/account-transfer-signup" component={AccountTransferSignUpPage} />
        <Route exact path="/reset-password" component={ResetPasswordPage} />
        <Route exact path="/forgot-password" component={ForgotPasswordPage} />
        <PrivateRoute path="/profiles" component={ProfilesPage} />
        <PrivateRoute exact path="/users" component={UsersPage} />
        <PrivateRoute exact path="/licenses" component={LicensesPage} />
        <PrivateRoute
          exact
          path="/"
          children={() => (
            <Redirect
              to={{
                pathname: '/users',
              }}
            />
          )}
        />
        <Route exact path="/tos" component={TOSPage} />
        <Route component={NoMatch} />
      </Switch>
    </Router>
  )
}
